import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import client from '../../api/client';

const TYPE_LABELS = { spread_value: 'Spread', moneyline_value: 'ML', total_value: 'Total', steam_move: 'Steam', reverse_line_movement: 'RLM' };
const TYPE_COLORS = {
  spread_value: 'bg-[rgba(59,130,246,0.1)] text-[#3b82f6]',
  moneyline_value: 'bg-[rgba(139,92,246,0.1)] text-[#8b5cf6]',
  total_value: 'bg-[rgba(245,158,11,0.1)] text-[#f59e0b]',
  steam_move: 'bg-[rgba(249,115,22,0.1)] text-[#f97316]',
  reverse_line_movement: 'bg-[rgba(168,85,247,0.1)] text-[#a855f7]',
};

const MAX_DISPLAY = 5;

function getMagnitude(alert) {
  const d = alert.discrepancy_data || {};
  if (d.difference) return d.difference;
  if (d.difference_pct) return d.difference_pct;
  if (d.magnitude) return d.magnitude;
  if (d.count) return d.count;
  return 0;
}

export default function EventAlerts({ eventId }) {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    client.get('/api/value-alerts')
      .then((r) => setAlerts((r.data || []).filter((a) => a.event_id === eventId)))
      .catch(() => setAlerts([]))
      .finally(() => setLoading(false));
  }, [eventId]);

  const sorted = useMemo(() =>
    [...alerts].sort((a, b) => getMagnitude(b) - getMagnitude(a)),
    [alerts],
  );

  const typeCounts = useMemo(() => {
    const counts = {};
    for (const a of alerts) {
      const t = a.alert_type || 'unknown';
      counts[t] = (counts[t] || 0) + 1;
    }
    return counts;
  }, [alerts]);

  if (loading || alerts.length === 0) return null;

  const displayed = sorted.slice(0, MAX_DISPLAY);
  const remaining = alerts.length - MAX_DISPLAY;

  return (
    <div className="rounded border border-[#fbbf24]/30 dark:border-[#78350f] bg-[rgba(251,191,36,0.04)] dark:bg-[rgba(251,191,36,0.03)] px-4 py-3">
      {/* Header with type counts */}
      <div className="flex items-center gap-2 flex-wrap mb-2">
        <p className="text-[10px] font-semibold uppercase tracking-[0.05em] text-[#b45309] dark:text-[#fbbf24]" style={{ fontFamily: 'var(--font-sans)' }}>
          {alerts.length} Alert{alerts.length > 1 ? 's' : ''}
        </p>
        {Object.entries(typeCounts).map(([type, count]) => (
          <span key={type} className={`text-[9px] font-semibold px-1 py-px rounded ${TYPE_COLORS[type] || 'bg-[#1a2030] text-[#8892a8]'}`}>
            {TYPE_LABELS[type] || type} {count}
          </span>
        ))}
      </div>

      {/* Top alerts */}
      <div className="space-y-1.5">
        {displayed.map((alert, i) => {
          const d = alert.discrepancy_data || {};
          const label = TYPE_LABELS[alert.alert_type] || alert.alert_type;
          const color = TYPE_COLORS[alert.alert_type] || 'bg-[#1a2030] text-[#8892a8]';
          let desc;
          if (alert.alert_type === 'steam_move') {
            const bks = (d.bookmakers_moved || []).join(', ');
            desc = `${d.count} books moved ${d.outcome_name} ${d.market === 'h2h' ? 'ML' : d.market} simultaneously (${bks})`;
          } else if (alert.alert_type === 'reverse_line_movement') {
            const oldF = d.old_value > 0 ? `+${d.old_value}` : d.old_value;
            const newF = d.new_value > 0 ? `+${d.new_value}` : d.new_value;
            desc = `Public on ${d.public_side}, line moved ${oldF}\u2192${newF} toward ${d.sharp_side}`;
          } else if (alert.alert_type === 'moneyline_value') {
            const p = d.bookmaker_price > 0 ? `+${d.bookmaker_price}` : d.bookmaker_price;
            const cip = d.consensus_implied_prob != null ? `${(d.consensus_implied_prob*100).toFixed(1)}%` : '—';
            desc = `${d.bookmaker_title}: ${d.outcome_name} at ${p} vs consensus ${cip}`;
          } else {
            const pt = d.bookmaker_point > 0 ? `+${d.bookmaker_point}` : d.bookmaker_point;
            const c = d.consensus_point > 0 ? `+${d.consensus_point}` : d.consensus_point;
            desc = `${d.bookmaker_title}: ${d.outcome_name} ${pt} vs consensus ${c}`;
          }
          return (
            <div key={alert.id || i} className="flex items-start gap-1.5 text-[11px]">
              <span className={`text-[9px] font-semibold px-1 py-px rounded whitespace-nowrap mt-px ${color}`}>{label}</span>
              <span className="text-[#475569] dark:text-[#8892a8]">{desc}</span>
            </div>
          );
        })}
      </div>

      {/* View all link */}
      {remaining > 0 && (
        <Link to="/alerts" className="block text-[10px] text-[#3b82f6] hover:text-[#2563eb] mt-2 transition-colors duration-150">
          View all {alerts.length} alerts &rarr;
        </Link>
      )}
    </div>
  );
}
